/* ServicesFooter — clean, responsive, dark-gradient footer mirroring the
 * Systems + Academy pattern, scoped to the Services (e-store) division.
 * Brand block spans 2 cols; then "Shop" and "Company" link columns; then
 * a thin compliance strip. All hrefs are real (no placeholder `#`).
 *
 * Mounted on: /services, /services/p/:slug, /services/checkout-success,
 * and the cart-central storefront at /store/*. Because the Services
 * storefront is e-commerce, the Shop column surfaces discovery routes
 * (Products, Wishlist, Cart, Orders, Support) rather than umbrella
 * cross-division links.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Linkedin, Facebook, Instagram, Youtube, Phone, Globe } from 'lucide-react';
import ZheltaLogo from './ZheltaLogo';
import DivisionBridge from './DivisionBridge';

export default function ServicesFooter() {
  return (
    <footer
      className="text-zinc-800 bg-[#F5F5F7]"
      data-testid="services-footer"
    >
      <section className="max-w-[1200px] mx-auto px-4 lg:px-6 py-12 grid grid-cols-1 md:grid-cols-4 gap-10">
        {/* Brand block — spans 2 cols on md+ */}
        <div className="md:col-span-2">
          <Link to="/services" className="inline-flex items-center gap-3 mb-4" data-testid="services-footer-brand">
            <ZheltaLogo size={36} />
            <div className="leading-none">
              <div className="text-[18px] font-extrabold tracking-[0.2em]">ZHELTA</div>
              <div className="text-[10px] font-semibold tracking-[0.3em] text-[#f5a623]">SERVICES</div>
            </div>
          </Link>
          <p className="text-[14px] text-zinc-600 leading-relaxed max-w-[480px]">
            Verified hardware, accessories and licences — ship-fast, vendor-neutral,
            with on-device setup and 24/7 post-purchase support by ZHELTA technicians.
          </p>

          <ul className="mt-5 flex flex-col sm:flex-row sm:flex-wrap gap-x-6 gap-y-2 text-zinc-500 text-[13px]">
            <li>
              <a
                href="tel:+19147681947"
                data-testid="services-footer-phone"
                className="inline-flex items-center gap-1.5 hover:text-[#c9a227]"
              >
                <Phone className="w-3.5 h-3.5 text-[#c9a227]" /> 914-768-1947
              </a>
            </li>
            <li>
              <a
                href="mailto:sales@zhelta.com"
                data-testid="services-footer-email"
                className="inline-flex items-center gap-1.5 hover:text-[#c9a227]"
              >
                <Mail className="w-3.5 h-3.5 text-[#c9a227]" /> sales@zhelta.com
              </a>
            </li>
            <li className="inline-flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-[#c9a227]" /> New York, NY · USA
            </li>
          </ul>
        </div>

        {/* Shop — services-only discovery routes, verified against live catalog */}
        <div>
          <h4 className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#f5a623] mb-3">Shop</h4>
          <ul className="space-y-2 text-[13.5px] text-zinc-700">
            <li><Link to="/services"                                   className="hover:text-[#c9a227]">All products</Link></li>
            <li><Link to="/store/products?category=laptops"            className="hover:text-[#c9a227]">Laptops</Link></li>
            <li><Link to="/store/products?category=desktops"           className="hover:text-[#c9a227]">Desktops</Link></li>
            <li><Link to="/store/products?category=monitors"           className="hover:text-[#c9a227]">Monitors</Link></li>
            <li><Link to="/store/products?category=accessories"        className="hover:text-[#c9a227]">Accessories</Link></li>
            <li><Link to="/store/products?category=software"           className="hover:text-[#c9a227]">Software &amp; licenses</Link></li>
            <li><Link to="/store/wishlist"                             className="hover:text-[#c9a227]">Wishlist</Link></li>
            <li><Link to="/store/cart"                                 className="hover:text-[#c9a227]">Cart &amp; checkout</Link></li>
          </ul>
        </div>

        {/* Company — services-scoped support */}
        <div>
          <h4 className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#f5a623] mb-3">Support</h4>
          <ul className="space-y-2 text-[13.5px] text-zinc-700">
            <li><Link to="/store/account"                 className="hover:text-[#c9a227]">My account</Link></li>
            <li><Link to="/store/orders"                  className="hover:text-[#c9a227]">My orders</Link></li>
            <li><Link to="/store/shipping"                className="hover:text-[#c9a227]">Shipping &amp; delivery</Link></li>
            <li><Link to="/store/returns"                 className="hover:text-[#c9a227]">Returns &amp; refunds</Link></li>
            <li><Link to="/store/warranty"                className="hover:text-[#c9a227]">Warranty</Link></li>
            <li><a href="mailto:support@zhelta.com"      className="hover:text-[#c9a227]">Email support</a></li>
            <li><Link to="/services/contact"               className="hover:text-[#c9a227]" data-testid="services-footer-contact-form">Contact form</Link></li>
          </ul>
        </div>
      </section>

      {/* Bridge to other divisions */}
      <DivisionBridge current="services" />

      {/* Compliance strip */}
      <section className="border-t border-zinc-200">
        <div className="max-w-[1200px] mx-auto px-4 lg:px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="text-[12px] text-zinc-500">
            <Link
              to="/copyright"
              data-testid="services-footer-copyright"
              className="hover:text-[#c9a227] transition"
              title="Copyright & Terms of Use"
            >
              © {new Date().getFullYear()} ZHELTA LLC. All rights reserved. ZHELTA Services is a division of ZHELTA LLC.
            </Link>
          </div>
          <div className="flex items-center gap-4 text-zinc-500">
            <Link
              to="/privacy"
              data-testid="services-footer-privacy"
              className="text-[11.5px] hover:text-[#c9a227]"
            >
              Privacy policy
            </Link>
            <Link
              to="/cookies"
              data-testid="services-footer-cookies"
              className="text-[11.5px] hover:text-[#c9a227]"
            >
              Cookies
            </Link>
            <a
              href="https://www.linkedin.com/company/zhelta-llc/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              data-testid="services-footer-linkedin"
              className="hover:text-[#c9a227]"
            >
              <Linkedin className="w-4 h-4" />
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61562259287313"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              data-testid="services-footer-facebook"
              className="hover:text-[#c9a227]"
            >
              <Facebook className="w-4 h-4" />
            </a>
            <a
              href="https://www.instagram.com/zhelta_support?igsh=dTdqbGo4MWZqcW1z&utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              data-testid="services-footer-instagram"
              className="hover:text-[#c9a227]"
            >
              <Instagram className="w-4 h-4" />
            </a>
            <a
              href="https://youtube.com/@zheltallc?si=x-hOD-nWZpWempMK"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
              data-testid="services-footer-youtube"
              className="hover:text-[#c9a227]"
            >
              <Youtube className="w-4 h-4" />
            </a>
            <Link
              to="/"
              className="text-[11.5px] hover:text-[#c9a227] inline-flex items-center gap-1"
              data-testid="services-footer-mainsite"
            >
              <Globe className="w-3 h-3" /> ZHELTA main site
            </Link>
          </div>
        </div>
      </section>
    </footer>
  );
}
